import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { RolloutEnvironmentRow } from '../../api/rollout';
import { useRolloutOverview } from '../../hooks/useRollout';
import { ago, runningLabel, WATCH_STATUS } from './rolloutModel';
import { Kpi, StateIcon } from './rolloutParts';
import './Rollout.scss';

type Stage = 'prod' | 'test' | 'all';

const STAGES: { id: Stage; label: string }[] = [
    { id: 'prod', label: 'Productie' },
    { id: 'test', label: 'Test' },
    { id: 'all', label: 'Alle' },
];

const useNow = () => {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 10000);

        return () => clearInterval(timer);
    }, []);

    return now;
};

/** An environment needs a look when a rollback light is red or a new kind of failure showed up. */
const needsLook = (row: RolloutEnvironmentRow) =>
    row.worst === 'Bad' || row.newProblems > 0;

export const envPath = (customer: string, environment: string) =>
    `/rollout/${encodeURIComponent(customer)}/${encodeURIComponent(
        environment
    )}`;

/** Every environment, with what was deployed and whether it asks for attention. */
export const RolloutOverview = () => {
    const [params, setParams] = useSearchParams();
    const stage = (params.get('stage') as Stage) ?? 'prod';
    const { rows, live, error } = useRolloutOverview();
    const navigate = useNavigate();
    const now = useNow();

    // Links from before the environment pages (alerts) name only the watch: "klant|omgeving|tijd".
    const oldWatch = params.get('watch');

    useEffect(() => {
        const [customer, environment] = (oldWatch ?? '').split('|');

        if (customer && environment)
            navigate(
                `${envPath(customer, environment)}?watch=${encodeURIComponent(
                    oldWatch!
                )}`,
                { replace: true }
            );
    }, [oldWatch, navigate]);

    const shown = useMemo(
        () =>
            (rows ?? [])
                .filter((r) => stage === 'all' || r.stage === stage)
                .sort(
                    (a, b) =>
                        Number(needsLook(b)) - Number(needsLook(a)) ||
                        Number(b.watchStatus === 'watching') -
                            Number(a.watchStatus === 'watching')
                ),
        [rows, stage]
    );

    const watching = shown.filter((r) => r.watchStatus === 'watching').length;
    const attention = shown.filter(needsLook).length;

    return (
        <main className="ro">
            <header className="ro-banner">
                <div>
                    <h2>Uitrol</h2>
                    <p>
                        Elke deploy bewaakt, een dag vóór en een dag na. Een
                        wacht start vanzelf zodra een app een nieuwe build
                        draait.
                    </p>
                </div>
                <div className="ro-banner__side">
                    <span className={`ro-live${live ? ' is-on' : ''}`}>
                        {live ? 'Live' : 'Niet verbonden'}
                    </span>
                    <Link
                        to="/rollout/checklist"
                        className="btn btn-sm btn-outline-secondary"
                    >
                        Afvinklijst
                    </Link>
                    <div
                        className="ro-segments"
                        role="group"
                        aria-label="Omgevingen"
                    >
                        {STAGES.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                className={stage === s.id ? 'is-active' : ''}
                                onClick={() => setParams({ stage: s.id })}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            <div className="ro-summary">
                <Kpi label="Bewaakt nu" value={watching} />
                <Kpi
                    label="Aandacht nodig"
                    value={attention}
                    tone={attention > 0 ? 'bad' : undefined}
                />
                <Kpi label="Omgevingen" value={shown.length} />
            </div>

            {error && <p className="ro-error">{error}</p>}

            <section className="ro-card" aria-label="Omgevingen">
                {rows === undefined ? (
                    <div className="ro-empty">Omgevingen laden…</div>
                ) : shown.length === 0 ? (
                    <div className="ro-empty">
                        <strong>Nog geen omgevingen</strong>
                        Het dashboard vergelijkt de builds van alle omgevingen
                        elke minuut; na de eerste ronde staan ze hier.
                    </div>
                ) : (
                    <div className="ro-scroll">
                        <table className="ro-table ro-table--rows">
                            <thead>
                                <tr>
                                    <th
                                        className="is-state"
                                        aria-label="Terugzetten"
                                    />
                                    <th>Omgeving</th>
                                    <th>Wacht</th>
                                    <th>Laatste deploy</th>
                                    <th>Build</th>
                                    <th className="is-num">Nieuwe problemen</th>
                                    <th className="is-num">
                                        Mislukt na deploy
                                    </th>
                                    <th className="is-num">Exact vandaag</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map((r) => (
                                    <tr
                                        key={`${r.customer}/${r.environment}`}
                                        className={
                                            needsLook(r)
                                                ? 'is-attention'
                                                : undefined
                                        }
                                        onClick={() =>
                                            navigate(
                                                envPath(
                                                    r.customer,
                                                    r.environment
                                                )
                                            )
                                        }
                                        tabIndex={0}
                                        onKeyDown={(e) =>
                                            e.key === 'Enter' &&
                                            navigate(
                                                envPath(
                                                    r.customer,
                                                    r.environment
                                                )
                                            )
                                        }
                                    >
                                        <td className="is-state">
                                            {r.worst ? (
                                                <StateIcon state={r.worst} />
                                            ) : (
                                                <span className="ro-muted">
                                                    –
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <strong>
                                                {r.customer} / {r.environment}
                                            </strong>{' '}
                                            {r.stage === 'test' && (
                                                <span className="ro-pill">
                                                    test
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {r.watchStatus ? (
                                                <span
                                                    className={`ro-pill ro-pill--${r.watchStatus}`}
                                                >
                                                    {
                                                        WATCH_STATUS[
                                                            r.watchStatus
                                                        ]
                                                    }
                                                </span>
                                            ) : (
                                                <span className="ro-muted">
                                                    geen
                                                </span>
                                            )}
                                        </td>
                                        <td>{ago(r.deployedAt, now)}</td>
                                        <td className="ro-mono">
                                            {runningLabel(
                                                r.buildNumbers,
                                                r.builds
                                            )}
                                        </td>
                                        <td className="is-num">
                                            {r.watchStatus
                                                ? r.newProblems
                                                : '–'}
                                        </td>
                                        <td className="is-num">
                                            {r.watchStatus
                                                ? r.failedAfter
                                                : '–'}
                                        </td>
                                        <td className="is-num">
                                            {r.exactDailyRemaining != null &&
                                            r.exactDailyLimit
                                                ? `${Math.round(
                                                      (r.exactDailyRemaining /
                                                          r.exactDailyLimit) *
                                                          100
                                                  )}% over`
                                                : '–'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </main>
    );
};
